'use client';
import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import { useBenzingaEdge } from '@benzinga/edge';
import { Table, ColumnDef } from '@benzinga/table';
import { GovernmentTrade } from '@benzinga/gov-trades';

import { Pagination, type PaginationData } from '@benzinga/core-ui';
import { fetchCongressionalTrades } from '../../../app/(site)/government-trades/trades-actions';

const FREE_TIER_ROW_LIMIT = 10;

const BzEdgeCTA = React.lazy(() => import('@benzinga/ads').then(module => ({ default: module.BzEdgeCTA })));

const OptimizelyBanner = dynamic(() => import('@benzinga/ads').then(module => module.OptimizelyBanner), {
  ssr: true,
});

interface GovernmentTableProps {
  columnsDef: ColumnDef<any>[];
  height: number;
  hiddenColumns?: string[];
  hideMostRows?: boolean;
  hideTable?: boolean;
  rowData: any[];
  title?: string;
  variant?: string;
  showPagination?: boolean;
  description?: string;
  autoHeight?: boolean;
  pageSize?: number;
  totalItems?: number;
  sortEnabled?: boolean;
  /** 'card' (default) keeps the standalone card frame; 'all-trades' matches the profile
   *  Recent Trades container, with the title/description as a header above the table. */
  layout?: 'card' | 'all-trades';
}

export const GovernmentTable = ({
  columnsDef,
  height,
  hiddenColumns = [],
  hideMostRows,
  hideTable,
  description = '',
  rowData,
  title = '',
  variant,
  showPagination = false,
  autoHeight = false,
  pageSize = 20,
  totalItems = 0,
  sortEnabled = true,
  layout = 'card',
}: GovernmentTableProps) => {
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [rows, setRows] = useState<GovernmentTrade[]>(rowData);
  const [currentPage, setCurrentPage] = useState(1);
  // `totalItems` is an upper bound (it sums per-member trade counts, which double-count
  // jointly filed disclosures), so shrink it to the real row count as soon as a fetched
  // page comes back empty or short.
  const [effectiveTotalItems, setEffectiveTotalItems] = useState(totalItems);
  const [paginationKey, setPaginationKey] = useState(0);
  const pageCache = useRef(new Map<number, GovernmentTrade[]>([[0, rowData]]));

  const resetPaginationToCurrentPage = () => setPaginationKey(key => key + 1);

  const { hasEdgeSubscription: hasPermission, isEdgeSubscriptionLoading, isLoggedIn } = useBenzingaEdge();
  const [mode, setMode] = useState('');
  const [paginationEnabled, setPaginationEnabled] = useState(false);

  useEffect(() => {
    const canPaginate = !isEdgeSubscriptionLoading && (hasPermission || showPagination);
    setPaginationEnabled(canPaginate);
    if (isEdgeSubscriptionLoading) {
      setMode('');
    } else if (showPagination && !hasPermission && currentPage > 1) {
      setMode(!isLoggedIn ? 'show-login' : 'show-bz-edge-ad full');
    } else if (showPagination && !hasPermission) {
      setMode('hide-bz-edge');
    } else if (!isLoggedIn) {
      setMode('show-login');
    } else if (!hasPermission && hideTable) {
      setMode('show-bz-edge-ad full');
    } else if (!hasPermission && hideMostRows) {
      setMode('show-bz-edge-ad');
    } else if (!hasPermission) {
      setMode('hide-bz-edge');
    } else setMode('');
  }, [currentPage, hasPermission, hideTable, hideMostRows, isLoggedIn, isEdgeSubscriptionLoading, showPagination]);

  const handlePageChanged = async (data: PaginationData) => {
    if (!paginationEnabled || isLoadingPage) {
      resetPaginationToCurrentPage();
      return;
    }
    if (!hasPermission) {
      setCurrentPage(data.nextPage);
      return;
    }
    const apiPage = Math.max(0, data.nextPage - 1);
    const cachedRows = pageCache.current.get(apiPage);
    if (cachedRows) {
      setRows(cachedRows);
      setCurrentPage(data.nextPage);
      return;
    }

    setIsLoadingPage(true);
    try {
      const newRows = await fetchCongressionalTrades({
        page: apiPage,
        pageSize: data.pageSize,
        sort: 'report_date:desc',
      });
      if (newRows.length === 0 && apiPage > 0) {
        setEffectiveTotalItems(apiPage * data.pageSize);
        resetPaginationToCurrentPage();
        return;
      }
      if (newRows.length > 0 && newRows.length < data.pageSize) {
        setEffectiveTotalItems(apiPage * data.pageSize + newRows.length);
      }
      pageCache.current.set(apiPage, newRows);
      setRows(newRows);
      setCurrentPage(data.nextPage);
    } catch (error) {
      console.error(error);
      resetPaginationToCurrentPage();
    } finally {
      setIsLoadingPage(false);
    }
  };

  useEffect(() => {
    setRows(rowData);
    setCurrentPage(1);
    pageCache.current = new Map([[0, rowData]]);
    setEffectiveTotalItems(rowData.length < pageSize ? rowData.length : totalItems);
  }, [rowData, pageSize, totalItems]);

  // Without Edge only the first page-worth of rows is readable; the rest sits behind the CTA.
  const visibleRows = hasPermission ? rows : rows.slice(0, FREE_TIER_ROW_LIMIT);

  const tableBody = (
    <div className={`gov-trades-table ${variant ?? ''} ${mode}`}>
      <Table
        autoHeight={autoHeight}
        columnsDef={columnsDef}
        height={height}
        hiddenColumns={hiddenColumns}
        rowData={visibleRows}
        sortEnabled={hasPermission && sortEnabled}
      />
      {showPagination && (
        <div
          aria-disabled={!paginationEnabled || isLoadingPage}
          className={`flex justify-end items-end mt-1 ${
            paginationEnabled && !isLoadingPage ? '' : 'pointer-events-none opacity-40'
          }`}
        >
          <Pagination
            key={paginationKey}
            defaultPage={currentPage}
            pageSize={pageSize}
            pageNeighbors={1}
            onPageChanged={handlePageChanged}
            totalItems={effectiveTotalItems}
          />
        </div>
      )}
      {mode == 'show-login' && (
        <div className="absolute top-1/4 left-0 translate-x-0 md:left-1/2 md:-translate-x-1/2">
          <OptimizelyBanner
            adType="gov-trades"
            buttonText="Sign up for Benzinga Edge"
            subtitle="Unlock all government trades and get access to the best trading ideas, research, and analysis."
            title="Access with Benzinga Edge"
            utmSource="government-trades"
          />
        </div>
      )}
      {mode.includes('show-bz-edge-ad') && (
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2">
          <BzEdgeCTA type="gov-trades" />
        </div>
      )}
    </div>
  );

  if (layout === 'all-trades') {
    return (
      <div className="all-trades all-trades-boxed">
        {(title || description) && (
          <div className="rounded-t-[10px] border border-b-0 border-[#e3e9f4] bg-white px-4 pb-3.5 pt-4">
            {title && (
              <h2 className="text-xl font-bold leading-tight text-bzgray-900 max-[800px]:text-lg">
                {title} <span className="edge-mini">EDGE</span>
              </h2>
            )}
            {description && <p className="mt-1 text-sm font-medium leading-snug text-bzgray-600">{description}</p>}
          </div>
        )}
        {tableBody}
      </div>
    );
  }

  return (
    <div className="relative flex flex-col justify-between overflow-hidden rounded-xl border border-bzblue-400 bg-white p-0">
      {(title || description) && (
        <div className="flex flex-col justify-between items-start">
          {title ? (
            <div>
              <h2 className="pb-1 text-2xl font-bold leading-8 text-bzgray-900 max-[800px]:text-xl">
                {title} <span className="edge-mini">EDGE</span>
              </h2>
            </div>
          ) : null}
          {description && <p className="min-h-10 text-base font-medium leading-5 text-bzgray-600 max-[800px]:text-sm">{description}</p>}
        </div>
      )}
      {tableBody}
    </div>
  );
};
